import { cur } from '@/lib/currency';
import { statementStatus } from '@/lib/reconStatus';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Upload, Trash2, Eye, AlertTriangle, Pencil, Check, X, FileText, Settings2, Landmark, CheckCircle2, Loader2, Download, Link2, Link2Off, Building2, Banknote, Wand2, Plus, Sparkles, CalendarCheck2, ChevronRight, ChevronDown, Clock } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { NewBadge } from '@/components/NewBadge';
import BankPaymentDialog from '@/components/BankPaymentDialog';
import BankCodeDictionaryDialog from '@/components/banking/BankCodeDictionaryDialog';
import { SmartGroupsTab } from '@/components/banking/SmartGroupsTab';
import { OrphanInboxCard } from '@/components/banking/OrphanInboxCard';
import { MonthlyReconciliationCard } from '@/components/banking/MonthlyReconciliationCard';
import { AutoBookedEntriesCard } from '@/components/banking/AutoBookedEntriesCard';
import { NotesTab } from '@/components/banking/NotesTab';
import { ReconciliationTab } from '@/components/banking/ReconciliationTab';
import { GlobalTxnSearchCard } from '@/components/banking/GlobalTxnSearchCard';
import { PartiesTab } from '@/components/banking/PartiesTab';
import { TransactionsTab } from '@/components/banking/TransactionsTab';
import MatchCandidatesDialog from '@/components/banking/MatchCandidatesDialog';
import { localToday } from '@/lib/dateRange';

const COLORS = ['#F5841F', '#22C55E', '#0EA5E9', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'];
const CAT_LABELS = { pos_sales: 'POS Sales', pos_fees: 'POS Fees', bank_fees: 'Bank Fees', vat_fees: 'VAT on Fees', internal_transfer: 'Internal Transfers', incoming_transfer: 'Incoming Transfers', outgoing_transfer: 'Outgoing Transfers', sadad_payment: 'SADAD Bills', sadad_refund: 'SADAD Refund', iqama_renewal: 'Iqama Renewal', salary: 'Salary', vat: 'VAT', refund: 'Refund', other_sales: 'Other Sales', other: 'Other' };

function PosRow({ pm, branches, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(pm.label || '');
  const [branchId, setBranchId] = useState(pm.branch_id || '');
  const handleSave = () => { onSave(pm.machine_id, branchId, label); setEditing(false); };
  const handleCancel = () => { setLabel(pm.label || ''); setBranchId(pm.branch_id || ''); setEditing(false); };
  if (editing) {
    return (
      <div className="flex gap-2 items-center p-2 border rounded-xl bg-info/10 border-info/30" data-testid={`pos-row-edit-${pm.machine_id}`}>
        <span className="font-mono text-xs w-40 truncate" title={pm.machine_id}>{pm.machine_id}</span>
        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label" className="h-7 w-28 text-xs" data-testid={`pos-label-input-${pm.machine_id}`} />
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger className="h-7 w-36 text-xs" data-testid={`pos-branch-select-${pm.machine_id}`}><SelectValue placeholder="Branch" /></SelectTrigger>
          <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-success hover:bg-success/10" onClick={handleSave} data-testid={`pos-save-${pm.machine_id}`}><Check size={14} /></Button>
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:bg-muted/50" onClick={handleCancel}><X size={14} /></Button>
      </div>
    );
  }
  return (
    <div className="flex gap-2 items-center p-2 border rounded-xl bg-success/5 group" data-testid={`pos-row-${pm.machine_id}`}>
      <span className="font-mono text-xs flex-1 truncate" title={pm.machine_id}>{pm.machine_id}</span>
      <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => setEditing(true)} data-testid={`pos-label-${pm.machine_id}`}>{label || 'No label'}</Badge>
      <Badge className="bg-success/20 text-success cursor-pointer hover:bg-success/30" onClick={() => setEditing(true)} data-testid={`pos-branch-${pm.machine_id}`}>{branches.find(b => b.id === pm.branch_id)?.name || 'Unmapped'}</Badge>
      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setEditing(true)} data-testid={`pos-edit-${pm.machine_id}`}><Pencil size={12} /></Button>
      <Button size="sm" variant="ghost" className="h-6 text-error" onClick={onDelete} data-testid={`pos-delete-${pm.machine_id}`}><Trash2 size={12} /></Button>
    </div>
  );
}

export default function BankStatementsPage() {
  const { t: _t } = useLanguage();
  // iter927 — per-user saved preference (reuses the existing localStorage
  // pattern, e.g. JournalEntriesPage). Keyed by user id so it never affects
  // other users; default OFF when no saved value.
  const _openItemsPrefKey = `bs_open_items_only:${(JSON.parse(localStorage.getItem('user') || '{}').id) || 'anon'}`;
  const [statements, setStatements] = useState([]);
  const [healthSummary, setHealthSummary] = useState({});  // iter515 — {statement_id: {score, label, recommendations, ...}}
  const [reconStatusMap, setReconStatusMap] = useState({}); // iter925 — {id: {txn_count, linked_count, open_count}} from /recon/inbox
  const [openItemsOnly, setOpenItemsOnly] = useState(() => localStorage.getItem(_openItemsPrefKey) === 'true'); // iter926 — quick filter; iter927 — persisted per user
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [posMachines, setPosMachines] = useState([]);
  const [detail, setDetail] = useState(null);
  // iter532 — Per-category fee booking status (Bank Fees / VAT / POS Fees / SADAD)
  // for the new "+ Book as Expense" buttons. Keyed by statement id so a fresh
  // viewDetail() doesn't show stale data.
  const [feeStatus, setFeeStatus] = useState(null);
  const [feeBookingLoading, setFeeBookingLoading] = useState('');
  // iter539 — Monthly completion tracker. Auto-loaded on mount; tells
  // the user "is January done?" at a glance with a per-month bar.
  const [monthly, setMonthly] = useState(null);
  const [monthlyExpanded, setMonthlyExpanded] = useState(null);  // ym
  // iter541 — Global "Auto-booked entries" history list + Undo button
  // so the user can review and reverse ANY auto-booking from one place.
  const [autoBookedEntries, setAutoBookedEntries] = useState([]);
  const [autoBookedExpanded, setAutoBookedExpanded] = useState(false);
  // iter542 — multi-select for bulk Undo
  const [autoBookedSelected, setAutoBookedSelected] = useState(new Set());
  // iter540 — Collapsible "Uploaded statements" groups by year-month so
  // a tenant with 50+ statements doesn't render a giant infinite scroll.
  // Stored as { [ym]: bool }; absent key defaults to "expanded" for the
  // 2 most-recent months, "collapsed" otherwise (computed at render).
  const [stmtGroupOpen, setStmtGroupOpen] = useState({});
  // iter538 — branch picker before booking. Holds the in-flight request:
  // { category, label, months: [{ym, amount, txns}], totalAmount, branchId }
  const [feeBookingDialog, setFeeBookingDialog] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({ bank_account_id: '', bank_name: '', branch_id: '' });
  // iter881 — Skipped rows banner + debug drawer state. Populated by the
  // upload response so operators can see exactly which rows the parser
  // dropped (bad regex, unparseable date, zero amount, etc.) without
  // halting the entire upload.
  const [lastUploadResult, setLastUploadResult] = useState(null); // {skipped_rows, skipped_count, smart_hooks, ...}
  const [showSkippedDrawer, setShowSkippedDrawer] = useState(false);
  const [showPosManager, setShowPosManager] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  // iter555/556 — Status filter for the POS Reconciliation table.
  // Possible values: 'all' | 'discrepancies' | 'matched' | 'bank_higher' |
  // 'app_higher' | 'bank_only' | 'app_only'.
  // Persisted in localStorage AND deep-linkable via `?recon=<value>` so
  // accountants can share an auditor a URL that lands directly on the
  // rows that need attention (iter575).
  const [reconStatusFilter, setReconStatusFilter] = useState('all');
  // iter557 — Quick Investigate dialog state moved into ReconciliationTab (iter570)
  // iter565 — Batch AI explanation state moved into ReconciliationTab (iter570)
  // iter567 — Month-End Notes list (loaded when statement detail opens)
  const [monthEndNotes, setMonthEndNotes] = useState([]);
  const loadMonthEndNotes = async () => {
    if (!detail?.id) return;
    try {
      const r = await api.get(`/bank-statements/${detail.id}/month-end-notes`);
      setMonthEndNotes(r.data?.notes || []);
    } catch {
      setMonthEndNotes([]);
    }
  };
  useEffect(() => {
    if (detail?.id) loadMonthEndNotes();
    else setMonthEndNotes([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);
  const deleteMonthEndNote = async (noteId) => {
    if (!window.confirm('Delete this month-end note? This cannot be undone.')) return;
    try {
      await api.delete(`/bank-statements/${detail.id}/month-end-notes/${noteId}`);
      toast.success('Note deleted');
      loadMonthEndNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };
  // iter558 — Auto-explain handler moved into ReconciliationTab (iter570)
  const [dragOver, setDragOver] = useState(false);
  const [partiesFilter, setPartiesFilter] = useState('all'); // all | matched | unmatched
  // iter715 Phase C — Find-match dialog state.
  const [matchDialog, setMatchDialog] = useState({ open: false, txnIdx: null });
  // iter486 — Party Matches tab: filter pill for entity type
  const [partyMatchFilter, setPartyMatchFilter] = useState('all'); // all | supplier | customer | employee | partner | online | beneficiary
  const [suppliers, setSuppliers] = useState([]);
  // iter461 — additional entity collections so the match-party dialog
  // can route to Customers / Employees / Partners as well as Suppliers.
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [partners, setPartners] = useState([]);
  const [matchRow, setMatchRow] = useState(null); // {name, iban, bank} being matched
  const [matchSupplierId, setMatchSupplierId] = useState('');
  // iter461 — which entity type the user is matching against (the
  // "Save" button posts to /banking/match-party with this discriminator).
  const [matchEntityType, setMatchEntityType] = useState('supplier');
  // Bank-payment dialog state — opened either from the toolbar button
  // (no seed) or from clicking a debit row in a statement detail (seeded
  // with the txn date/amount/description so the user just picks the payee).
  const [bankPaymentOpen, setBankPaymentOpen] = useState(false);
  const [bankPaymentSeed, setBankPaymentSeed] = useState(null);
  // iter445 — bank-code dictionary editor (opened from decoded-label badges)
  const [dictState, setDictState] = useState({ open: false, code: '', label: '' });
  // Per-transaction manual link (Transactions tab "+ Link" button)
  const [linkTxn, setLinkTxn] = useState(null); // { index, description, link_type, link_id, link_name }
  const [linkEntityType, setLinkEntityType] = useState('supplier');
  const [linkEntityId, setLinkEntityId] = useState('');
  const [linkEntitySearch, setLinkEntitySearch] = useState('');
  const [linkExpenses, setLinkExpenses] = useState([]);

  const loadLinkExpenses = async () => {
    try {
      const r = await api.get('/expenses', { params: { limit: 500 } });
      const arr = Array.isArray(r.data) ? r.data : r.data?.data || [];
      setLinkExpenses(arr);
    } catch {
      setLinkExpenses([]);
    }
  };

  const saveManualLink = async () => {
    if (!linkTxn || !linkEntityId) { toast.error('Pick an entity'); return; }
    try {
      await api.post(`/bank-statements/${detail.id}/transactions/${linkTxn.index}/manual-link`, {
        entity_type: linkEntityType,
        entity_id: linkEntityId,
      });
      toast.success('Transaction linked');
      setLinkTxn(null);
      setLinkEntityId('');
      setLinkEntitySearch('');
      if (detail?.id) viewDetail(detail.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link');
    }
  };

  const clearManualLink = async () => {
    if (!linkTxn) return;
    try {
      await api.delete(`/bank-statements/${detail.id}/transactions/${linkTxn.index}/manual-link`);
      toast.success('Link cleared');
      setLinkTxn(null);
      if (detail?.id) viewDetail(detail.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };
  const fileInputRef = useRef(null);
  const uploadCardRef = useRef(null);
  // iter564 — Orphan Inbox: lists downstream records pointing at a
  // deleted statement so the user can one-click rebind (after re-uploading
  // the same file) or permanently detach them.
  const [orphans, setOrphans] = useState({ total: 0, rebindable: 0, items: [] });
  const [orphansExpanded, setOrphansExpanded] = useState(false);
  const [orphansBusy, setOrphansBusy] = useState(false);
  const loadOrphans = async () => {
    try {
      const r = await api.get('/bank-statements/orphans-inbox');
      setOrphans(r.data || { total: 0, rebindable: 0, items: [] });
    } catch {
      // silent — feature is opportunistic
    }
  };
  const orphanRebind = async (orph) => {
    setOrphansBusy(true);
    try {
      await api.post(`/bank-statements/orphans-inbox/${orph.type}/${orph.id}/rebind`);
      toast.success('Re-bound to the matching statement');
      await loadOrphans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-bind failed');
    } finally {
      setOrphansBusy(false);
    }
  };
  const orphanRebindAllMatching = async () => {
    setOrphansBusy(true);
    try {
      const r = await api.post('/bank-statements/orphans-inbox/rebind-all-matching');
      const { rebound = 0, skipped = 0 } = r.data || {};
      if (rebound > 0) toast.success(`Re-bound ${rebound} orphan record${rebound === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped — no fresh statement match)` : ''}`);
      else toast.info('Nothing to rebind — no orphans have a matching fresh statement yet.');
      await loadOrphans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk re-bind failed');
    } finally {
      setOrphansBusy(false);
    }
  };

  // iter710 — One-click maintenance: backfill `period_start` / `period_end`
  // on legacy statements so the new (and accurate) month-grouping works on
  // old uploads too. Always does a dry-run preview first so the admin can
  // confirm before any writes. Idempotent + safe to re-run.
  const [restampBusy, setRestampBusy] = useState(false);
  const restampLegacyPeriods = async () => {
    setRestampBusy(true);
    try {
      const preview = await api.post('/bank-statements/restamp-periods?dry_run=true');
      const { scanned = 0, updated = 0, examples = [] } = preview.data || {};
      if (updated === 0) {
        toast.success(`All ${scanned} statement(s) already have correct period dates — nothing to re-stamp.`);
        return;
      }
      const sample = examples.slice(0, 3).map((ex) => `· ${ex.file_name}: ${ex.before.period_start || '(missing)'} → ${ex.after.period_start}`).join('\n');
      if (!window.confirm(
        `Re-stamp ${updated} of ${scanned} statement(s)?\n\n` +
        `This fills in the new period_start / period_end fields from each statement's transaction dates (min → max), so older uploads group correctly by month.\n\n` +
        `Sample changes:\n${sample}\n\n` +
        `Existing transactions / totals / reconciliation links stay UNTOUCHED. Idempotent — re-running does nothing.`
      )) return;
      const r = await api.post('/bank-statements/restamp-periods');
      toast.success(`Re-stamped ${r.data?.updated || 0} legacy statement(s).`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-stamp failed');
    } finally {
      setRestampBusy(false);
    }
  };
  const orphanDetach = async (orph) => {
    if (!window.confirm('Detach this record permanently as a standalone entry? It will stay in your books but will no longer be tied to any statement.')) return;
    setOrphansBusy(true);
    try {
      await api.post(`/bank-statements/orphans-inbox/${orph.type}/${orph.id}/detach`);
      toast.success('Detached permanently');
      await loadOrphans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Detach failed');
    } finally {
      setOrphansBusy(false);
    }
  };
  const [highlightUpload, setHighlightUpload] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // iter575 — Sync `reconStatusFilter` with the `?recon=<value>` URL param
  // and localStorage. Priority on init: URL param → localStorage → 'all'.
  // Whenever the filter changes, both the URL and localStorage are updated
  // (URL is `replace` to avoid polluting browser history on every pill click).
  useEffect(() => {
    const fromUrl = searchParams.get('recon');
    if (fromUrl) {
      setReconStatusFilter(fromUrl);
      return;
    }
    try {
      const saved = localStorage.getItem('recon_status_filter');
      if (saved) { setReconStatusFilter(saved); return; }
      if (localStorage.getItem('recon_show_only_discrepancies') === '1') {
        setReconStatusFilter('discrepancies');
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setReconStatusFilterPersisted = (v) => {
    setReconStatusFilter(v);
    try { localStorage.setItem('recon_status_filter', v); } catch { /* noop */ }
    const next = new URLSearchParams(searchParams);
    if (v && v !== 'all') next.set('recon', v); else next.delete('recon');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => { fetchData(); _fetchBr(); loadOrphans(); }, []);

  // iter925 — refresh recon status (and statements) when the tab regains focus
  // / becomes visible, so status made elsewhere (Inbox, another user) shows the
  // latest state. Reuses fetchData; no polling, no new infrastructure.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') fetchData(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter927 — persist the "Open items only" choice per user (reuses existing
  // localStorage preference pattern; frontend-only, no backend).
  useEffect(() => {
    localStorage.setItem(_openItemsPrefKey, String(openItemsOnly));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemsOnly]);

  // iter576 — Open a statement automatically when `?statement=<id>` is set.
  // Runs once on mount so an auditor's deep-link lands them right on the
  // detail card. Stripping the param happens on explicit Close.
  useEffect(() => {
    const sid = searchParams.get('statement');
    if (sid) {
      viewDetail(sid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter540 — Pulls the monthly completion summary so the user can see
  // "is January closed?" at a glance. Re-fetched whenever a link/upload/
  // delete action changes the underlying txn link status.
  const fetchMonthlyCompletion = async () => {
    try {
      const r = await api.get('/bank-statements/monthly-completion?months_back=12');
      setMonthly(r.data || null);
    } catch { /* noop */ }
    // iter541 — auto-booked entries history (cheap, parallel)
    try {
      const r2 = await api.get('/bank-statements/auto-booked-entries-list?limit=200');
      setAutoBookedEntries(r2.data?.entries || []);
    } catch { /* noop */ }
  };

  // iter541 — Undo a single auto-booked entry from the history panel.
  const undoAutoBookedEntry = async (markerId) => {
    if (!window.confirm('Undo this booking? The linked expense will be deleted and the bank line will become pending again.')) return;
    try {
      await api.delete(`/bank-statements/auto-booked-entries-list/${markerId}`);
      toast.success('Undone — pending again');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
  };

  // iter542 — Bulk-Undo selected entries from the history table.
  const bulkUndoSelected = async () => {
    const ids = Array.from(autoBookedSelected);
    if (ids.length === 0) { toast.error('Pick at least one entry'); return; }
    if (!window.confirm(`Undo ${ids.length} entr${ids.length === 1 ? 'y' : 'ies'}? Their linked expenses will be deleted.`)) return;
    try {
      const r = await api.post('/bank-statements/auto-booked-entries-list/bulk-undo', { marker_ids: ids });
      const u = (r.data?.undone || []).length;
      const nf = (r.data?.not_found || []).length;
      toast.success(`Undone ${u} entr${u === 1 ? 'y' : 'ies'}${nf > 0 ? ` · ${nf} not found` : ''}`);
      setAutoBookedSelected(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk Undo failed');
    }
  };

  // iter542 — Export the auto-booked history as CSV for accountant audit.
  const exportAutoBookedCsv = async () => {
    try {
      const r = await api.get('/bank-statements/auto-booked-entries-csv', { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auto-booked-entries-${localToday()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  // Deep-link: when arriving from "Bank Accounts" with ?account=<id> we
  // pre-select that account, scroll the upload zone into view, and pulse
  // a ring on the card so the user immediately knows where to drop the
  // file. The query param is stripped after handling so a refresh doesn't
  // re-trigger the highlight.
  useEffect(() => {
    const accountId = searchParams.get('account');
    if (!accountId || !bankAccounts.length) return;
    const acc = bankAccounts.find(a => a.id === accountId);
    if (!acc) return;
    onPickAccount(accountId);
    // Defer scroll until after render commits
    setTimeout(() => {
      uploadCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightUpload(true);
      setTimeout(() => setHighlightUpload(false), 2400);
    }, 100);
    // Strip the param so the highlight is one-shot
    const next = new URLSearchParams(searchParams);
    next.delete('account');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccounts]);
  const fetchData = async () => {    try {
      const [sR, pR, bR, hR, rR] = await Promise.all([
        api.get('/bank-statements'),
        api.get('/pos-machines'),
        api.get('/bank-accounts').catch(() => ({ data: [] })),
        api.get('/bank-statements/health-summary').catch(() => ({ data: { summaries: [] } })),
        // Reuse the EXISTING Reconciliation Inbox list endpoint so the status
        // dots here are computed from the exact same counts as the Inbox.
        api.get('/recon/inbox?limit=200').catch(() => ({ data: { statements: [] } })),
      ]);
      setStatements(sR.data);
      setPosMachines(pR.data);
      setBankAccounts(bR.data || []);
      // iter515 — index health summaries by statement_id for O(1) lookup on each card
      const map = {};
      for (const h of (hR.data?.summaries || [])) { map[h.statement_id] = h; }
      setHealthSummary(map);
      // iter925 — index recon status (txn_count/linked_count/open_count) by id
      const rmap = {};
      for (const st of (rR.data?.statements || [])) { rmap[st.id] = st; }
      setReconStatusMap(rmap);
      // Suppliers are light — fetch once for the match dialog
      try { const sup = await api.get('/suppliers'); setSuppliers(sup.data || []); } catch { /* noop */ }
      // iter540 — Monthly completion tracker (loaded async, non-blocking)
      fetchMonthlyCompletion();
      // iter461 — load the other 3 entity collections so the match-party
      // dialog can offer them as targets. All four are lazy-fetched in
      // parallel; each silently no-ops on error so a missing perm on,
      // say, employees doesn't break the supplier flow.
      try {
        const [cR, eR, prR] = await Promise.all([
          api.get('/customers').catch(() => ({ data: [] })),
          api.get('/employees').catch(() => ({ data: [] })),
          api.get('/partners').catch(() => ({ data: [] })),
        ]);
        // iter471 — /api/customers is paginated → returns {data:[...]}.
        // /employees + /partners return a raw array. Normalize defensively
        // so `.map()` in the dropdown never sees a non-array.
        const asArray = (r) => Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : []);
        setCustomers(asArray(cR.data));
        setEmployees(asArray(eR.data));
        setPartners(asArray(prR.data));
      } catch { /* noop */ }
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  const [restamping, setRestamping] = useState(false);
  const handleRestampPeriods = async () => {
    setRestamping(true);
    try {
      const { data } = await api.post('/bank-statements/restamp-periods');
      const parts = [`Scanned ${data.scanned}`, `${data.updated} restamped`];
      if (data.dates_normalised) parts.push(`${data.dates_normalised} dates fixed`);
      toast.success(`Periods restamped — ${parts.join(', ')}`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restamp periods');
    } finally {
      setRestamping(false);
    }
  };

  const selectedAccount = useMemo(
    () => bankAccounts.find(a => a.id === uploadData.bank_account_id),
    [bankAccounts, uploadData.bank_account_id]
  );

  const onPickAccount = (id) => {
    const acc = bankAccounts.find(a => a.id === id);
    if (!acc) { setUploadData({ bank_account_id: '', bank_name: '', branch_id: '' }); return; }
    const ids = (acc.branch_ids && acc.branch_ids.length) ? acc.branch_ids : (acc.branch_id ? [acc.branch_id] : []);
    setUploadData({
      bank_account_id: acc.id,
      bank_name: acc.bank_name || acc.name || '',
      branch_id: ids[0] || '',
    });
  };

  const [reviewOnly, setReviewOnly] = useState(true);
  const onToggleReviewOnly = (checked) => {
    if (!checked && !window.confirm('Switch to NORMAL mode?\n\nIn normal mode the upload may automatically match rows, settle payments and create journals (Auto-reconcile is on). Use it only for current statements.\n\nHistorical statements must stay in Review Only.')) return;
    setReviewOnly(checked);
  };
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('bank_name', uploadData.bank_name || '');
      form.append('branch_id', uploadData.branch_id || '');
      form.append('bank_account_id', uploadData.bank_account_id || '');
      form.append('review_only', reviewOnly ? 'true' : 'false');
      const res = await api.post('/bank-statements/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const c = res.data.counts;
      const countsTxt = c ? ` · ${c.duplicates_skipped} duplicates skipped · ${c.invalid_rows} invalid · ${c.non_transaction_rows} header/summary rows ignored` : '';
      toast.success((res.data.review_only ? `Imported ${res.data.transactions_parsed} rows for review — nothing linked or booked` : `Imported ${res.data.transactions_parsed} transactions`) + countsTxt, { duration: 8000 });
      // iter881 — Cache the upload response so the "Parse skipped X rows"
      // banner + "Show skipped rows" drawer can render below the upload
      // card until the next upload or manual dismiss.
      setLastUploadResult(res.data);
      // iter920 — Auto-reconcile-on-upload summary toast (reuses the same
      // upload response counts shown in the summary banner below).
      const ar = res.data?.auto_reconcile;
      if (ar?.ran) {
        const c = ar.counts || {};
        const parts = [];
        if (c.auto_reconciled) parts.push(`${c.auto_reconciled} auto-reconciled`);
        if (c.needs_confirm) parts.push(`${c.needs_confirm} to confirm`);
        if (c.review) parts.push(`${c.review} to review`);
        if (parts.length) toast.success(`Reconciliation: ${parts.join(' · ')}`, { duration: 6000 });
      }
      const skippedCount = res.data?.skipped_count || 0;
      if (skippedCount > 0) {
        toast.warning(
          `Parse skipped ${skippedCount} row${skippedCount === 1 ? '' : 's'} — click "Show skipped rows" for details`,
          { duration: 6000 }
        );
      }
      // iter882 — Smart hook summary toast (supplier payments created,
      // loans settled, etc.).
      const sh = res.data?.smart_hooks;
      if (sh && sh.total_actions > 0) {
        const parts = [];
        if (sh.created_supplier_payments) parts.push(`${sh.created_supplier_payments} supplier payment${sh.created_supplier_payments === 1 ? '' : 's'} created`);
        if (sh.created_partner_entries) parts.push(`${sh.created_partner_entries} partner entr${sh.created_partner_entries === 1 ? 'y' : 'ies'} created`);
        if (sh.loans_settled) parts.push(`${sh.loans_settled} loan installment${sh.loans_settled === 1 ? '' : 's'} paid`);
        if (sh.salaries_settled) parts.push(`${sh.salaries_settled} salar${sh.salaries_settled === 1 ? 'y' : 'ies'} settled`);
        if (sh.sales_paid) parts.push(`${sh.sales_paid} sale${sh.sales_paid === 1 ? '' : 's'} marked paid`);
        if (parts.length) toast.success(`Smart hooks: ${parts.join(' · ')}`, { duration: 8000 });
      }
      // iter883 — Missing-record + auto-booking toasts.
      const missing = res.data?.missing_record_count || 0;
      if (missing > 0) {
        toast.warning(
          `${missing} txn${missing === 1 ? '' : 's'} couldn't be matched — check Notification Center for details`,
          { duration: 7000 }
        );
      }
      const ab = res.data?.auto_book;
      if (ab && ab.booked > 0) {
        toast.success(`Auto-booked ${ab.booked} journal entr${ab.booked === 1 ? 'y' : 'ies'} for high-confidence matches`, { duration: 7000 });
      }
      // Surface the beneficiary auto-link summary as a secondary toast so
      // the user can see how many txns were linked vs flagged unassigned.
      const st = res.data.autolink_stats;
      if (st && st.total > 0) {
        const linked = (st.matched_iban || 0) + (st.matched_account || 0)
          + (st.matched_name_exact || 0) + (st.matched_name_partial || 0);
        if (linked > 0 || st.unassigned > 0) {
          const parts = [];
          if (linked > 0) parts.push(`${linked} auto-linked to beneficiaries`);
          if (st.unassigned > 0) parts.push(`${st.unassigned} unassigned — needs review`);
          toast.info(parts.join(' · '));
        }
      }
      // iter833 — Pass-through advance auto-settlement feedback. Backend
      // now runs auto-recon inline on every upload and returns a summary
      // block so we can surface "N advances auto-settled" straight away
      // instead of waiting for the overnight cron banner.
      const adv = res.data?.advance_recon;
      if (adv && adv.linked_count > 0) {
        const cur = res.data?.currency || '';
        toast.success(
          `${adv.linked_count} pass-through advance${adv.linked_count === 1 ? '' : 's'} auto-settled · ${cur} ${Number(adv.total_amount || 0).toFixed(2)}`,
          { duration: 8000 },
        );
      }
      fetchData();
      viewDetail(res.data.id);
      // Trigger auto-match across all bank/cash COA accounts so freshly-
      // uploaded txns are paired with existing JEs immediately. iter390.
      if (!res.data.review_only) autoRescan({ silentNoMatch: true });
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const [rescanning, setRescanning] = useState(false);
  const autoRescan = async ({ silentNoMatch = false } = {}) => {
    setRescanning(true);
    try {
      const r = await api.post('/accounting/reconciliation/auto-match-all', {
        window_days: 3,
      });
      const d = r.data || {};
      const matched = d.total_matched || 0;
      if (matched > 0) {
        toast.success(`Re-scan complete · ${matched} match${matched === 1 ? '' : 'es'} across ${d.accounts_scanned} account(s)`);
      } else if (!silentNoMatch) {
        toast(`Re-scan complete · 0 new matches (scanned ${d.accounts_scanned} account(s) · ${d.total_je_scanned} JEs)`);
      }
    } catch (e) {
      // Don't block upload flow on a rescan failure
      console.warn('Auto-rescan failed:', e?.response?.data?.detail);
    }
    setRescanning(false);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleUpload(f);
  };

  const viewDetail = async (id) => {
    try {
      const [dR, aR] = await Promise.all([api.get(`/bank-statements/${id}`), api.get(`/bank-statements/${id}/analysis`)]);
      setDetail(dR.data); setAnalysis(aR.data);
      // iter576 — Sync the opened statement to `?statement=<id>` so the URL
      // is deep-linkable. Use replace so pill clicks don't pollute history.
      try {
        const next = new URLSearchParams(window.location.search);
        next.set('statement', id);
        setSearchParams(next, { replace: true });
      } catch { /* noop */ }
      // iter532 — Fee booking status (Bank Fees / VAT / POS Fees / SADAD)
      try {
        const fR = await api.get(`/bank-statements/${id}/fee-booking-status`);
        setFeeStatus(fR.data);
      } catch { setFeeStatus(null); }
      setReconLoading(true);
      try {
        const rR = await api.get(`/bank-statements/${id}/reconciliation`);
        setReconciliation(rR.data);
      } catch { setReconciliation(null); }
      finally { setReconLoading(false); }
      // Scroll to detail smoothly
      setTimeout(() => {
        document.getElementById('statement-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch { toast.error('Failed'); }
  };

  // iter538 — Click handler: open the branch-picker dialog with a
  // preview of which months will be booked. The actual POST is done by
  // `confirmBookFeeCategory` once the user picks a branch.
  const bookFeeCategory = (categoryKey) => {
    if (!detail?.id) return;
    const fcat = feeStatus?.categories?.[categoryKey];
    if (!fcat) return;
    const unbookedMonths = Object.entries(fcat.months || {})
      .filter(([, m]) => !m.booked && m.bookable !== false)
      .map(([ym, m]) => ({ ym, amount: m.amount || 0, txns: m.txns || 0 }))
      .sort((a, b) => a.ym.localeCompare(b.ym));
    if (unbookedMonths.length === 0) {
      toast.info(`${fcat.label}: nothing ready to book (already booked, statement month incomplete, or needs review)`);
      return;
    }
    setFeeBookingDialog({
      category: categoryKey,
      label: fcat.label,
      months: unbookedMonths,
      totalAmount: unbookedMonths.reduce((s, m) => s + m.amount, 0),
      branchId: detail.branch_id || '',  // pre-fill with statement's branch if known
    });
  };

  const confirmBookFeeCategory = async () => {
    if (!feeBookingDialog || !detail?.id) return;
    const { category, label, branchId } = feeBookingDialog;
    setFeeBookingLoading(category);
    try {
      const r = await api.post(`/bank-statements/${detail.id}/book-fee-category`, {
        category,
        branch_id: branchId || null,
      });
      const { created = [], skipped = [] } = r.data || {};
      if (created.length > 0) {
        const total = created.reduce((s, c) => s + (c.amount || 0), 0);
        toast.success(`Booked ${created.length} ${label} entr${created.length === 1 ? 'y' : 'ies'} (${cur()} ${total.toFixed(2)})${skipped.length ? ` · ${skipped.length} already booked` : ''}`);
      } else if (skipped.length > 0) {
        toast.info(`${label} already booked for ${skipped.map(s => s.year_month).join(', ')}`);
      }
      const fR = await api.get(`/bank-statements/${detail.id}/fee-booking-status`);
      setFeeStatus(fR.data);
      setFeeBookingDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to book');
    } finally {
      setFeeBookingLoading('');
    }
  };


  const savePosMapping = async (machineId, branchId, label) => {
    try { await api.post('/pos-machines', { machine_id: machineId, branch_id: branchId, label }); toast.success('Saved'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const linkStatementToAccount = async (stmtId, bankAccountId) => {
    try {
      await api.put(`/bank-statements/${stmtId}/link`, { bank_account_id: bankAccountId || null });
      toast.success(bankAccountId ? 'Linked to bank account' : 'Unlinked');
      fetchData();
      if (detail?.id === stmtId) {
        // refresh detail header to show new linkage
        const dR = await api.get(`/bank-statements/${stmtId}`);
        setDetail(dR.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    }
  };

  // iter485 — Toggle the statement's review status ("done" vs "pending").
  // A done statement gets a green tick badge in the list view + on the
  // detail header so the user can see at a glance which uploads have
  // already been fully reconciled / labelled / matched.
  const toggleReviewed = async (stmtId, currentStatus) => {
    const next = currentStatus === 'done' ? 'pending' : 'done';
    try {
      await api.put(`/bank-statements/${stmtId}/review-status`, { status: next });
      toast.success(next === 'done' ? 'Marked as reviewed' : 'Re-opened for review');
      // Reflect in detail + list
      if (detail?.id === stmtId) {
        setDetail(d => d ? { ...d, review_status: next } : d);
      }
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update review status');
    }
  };

  const saveMatchToSupplier = async () => {
    if (!matchRow || !matchSupplierId) {
      toast.error(`Pick a ${matchEntityType}`);
      return;
    }
    try {
      // iter461 — route through the generic /banking/match-party endpoint
      // so Customer / Employee / Partner are first-class match targets too.
      await api.post('/banking/match-party', {
        entity_type: matchEntityType,
        entity_id: matchSupplierId,
        name: matchRow.name,
        iban: (matchRow.iban || [])[0] || '',
        bank: (matchRow.bank || [])[0] || '',
      });
      toast.success('Matched — next time this party will be auto-linked');
      setMatchRow(null); setMatchSupplierId(''); setMatchEntityType('supplier');
      // Re-fetch analysis so the new match shows up instantly
      if (detail?.id) {
        const aR = await api.get(`/bank-statements/${detail.id}/analysis`);
        setAnalysis(aR.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match');
    }
  };

  const fmt = (v) => `${cur()} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmt0 = (v) => `${cur()} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const branchName = (id) => branches.find(b => b.id === id)?.name || '—';

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* What is this page? — collapsible orientation help. */}
        <PageInfoCallout
          storageKey="bank-statements"
          title="What is the Bank Statements module?"
        >
          <p>
            Upload your bank&apos;s monthly statement (CSV/Excel/PDF) and we&apos;ll <strong>auto-match</strong> each
            line to your POS sales, supplier payments, and platform settlements — so reconciling a month takes
            minutes instead of hours. Re-uploading the same statement is automatically de-duplicated by SHA-256
            hash, and deleting a statement <em>never</em> removes the payments you already linked from it.
          </p>
        </PageInfoCallout>

        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <FileText size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground flex items-center gap-2" data-testid="bankstatements-title">
              Bank Statements
              <NewBadge since="2026-04-24" featureId="bank-statements-top3-picker" size="xs" label="Top-3 picker" />
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Upload, analyze & reconcile bank statements against your POS & sales</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-xl border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-300"
              onClick={() => { setBankPaymentSeed(null); setBankPaymentOpen(true); }}
              data-testid="bank-payment-btn"
            >
              <Banknote size={16} className="mr-2" /> Record Bank Payment
            </Button>
            <Button variant="outline" className="rounded-xl" onClick={() => setShowPosManager(true)} data-testid="pos-settings-btn">
              <Settings2 size={16} className="mr-2" /> POS Mapping
            </Button>
            <Button
              variant="outline"
              className="rounded-xl border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-950/20 dark:border-amber-800 dark:text-amber-300"
              onClick={handleRestampPeriods}
              disabled={restamping}
              title="Backfill statement periods & fix undated transactions on older uploads"
              data-testid="restamp-periods-btn"
            >
              {restamping ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CalendarCheck2 size={16} className="mr-2" />}
              {restamping ? 'Restamping…' : 'Restamp Periods'}
            </Button>
          </div>
        </div>

        {/* Global transaction search — scans every month/statement */}
        <GlobalTxnSearchCard onViewStatement={viewDetail} />

        {/* Upload zone — simplified flow: pick bank account → drop file */}
        <Card
          ref={uploadCardRef}
          className={`border-border overflow-hidden transition-all duration-300 ${
            highlightUpload ? 'ring-4 ring-primary/40 ring-offset-2 shadow-lg' : ''
          }`}
          data-testid="upload-card"
        >
          <CardContent className="p-0">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Left: Context */}
              <div className="p-5 bg-gradient-to-br from-sky-50 to-emerald-50 border-r border-border lg:col-span-1">
                <div className="flex items-center gap-2 mb-3">
                  <Landmark size={18} className="text-info" />
                  <span className="text-sm font-semibold text-foreground">Upload target</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Bank account</Label>
                    <Select value={uploadData.bank_account_id || 'manual'} onValueChange={(v) => onPickAccount(v === 'manual' ? '' : v)}>
                      <SelectTrigger className="h-10 bg-white" data-testid="upload-bank-account-select">
                        <SelectValue placeholder="Pick saved account" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual entry</SelectItem>
                        {bankAccounts.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name} — {a.bank_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!uploadData.bank_account_id && (
                    <>
                      <div>
                        <Label className="text-xs text-muted-foreground">Bank name</Label>
                        <Input value={uploadData.bank_name} onChange={(e) => setUploadData({ ...uploadData, bank_name: e.target.value })} placeholder="e.g. Alinma, Al Bilad" className="h-10 bg-white" data-testid="upload-bank-name-input" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Branch (optional)</Label>
                        <Select value={uploadData.branch_id || 'all'} onValueChange={(v) => setUploadData({ ...uploadData, branch_id: v === 'all' ? '' : v })}>
                          <SelectTrigger className="h-10 bg-white" data-testid="upload-branch-select"><SelectValue placeholder="All branches" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All branches</SelectItem>
                            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  {selectedAccount && (
                    <div className="text-xs text-muted-foreground bg-white/70 rounded-lg p-2 border border-border">
                      <div className="flex items-center gap-1.5 text-success font-medium mb-1">
                        <CheckCircle2 size={12} /> Auto-filled
                      </div>
                      <div className="text-muted-foreground">Bank: <span className="text-foreground">{selectedAccount.bank_name}</span></div>
                      <div className="text-muted-foreground">
                        Branches: <span className="text-foreground">
                          {(() => {
                            const ids = (selectedAccount.branch_ids && selectedAccount.branch_ids.length)
                              ? selectedAccount.branch_ids
                              : (selectedAccount.branch_id ? [selectedAccount.branch_id] : []);
                            if (ids.length === 0) return 'All branches';
                            return ids.map(branchName).join(', ');
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                  <label className={`flex items-start gap-2 text-xs rounded-lg p-2 border cursor-pointer ${reviewOnly ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-400'}`} data-testid="upload-review-only-label">
                    <input type="checkbox" className="mt-0.5" checked={reviewOnly} onChange={(e) => onToggleReviewOnly(e.target.checked)} data-testid="upload-review-only-checkbox" />
                    <span>
                      <span className={`inline-block mb-1 px-1.5 py-0.5 rounded font-semibold text-white ${reviewOnly ? 'bg-emerald-600' : 'bg-amber-500'}`} data-testid="upload-mode-badge">
                        {reviewOnly ? 'Mode: REVIEW ONLY' : 'Mode: NORMAL'}
                      </span><br />
                      {reviewOnly
                        ? <>Historical import — rows stay unmatched. No automatic linking, payments, journals or settlements, now or by later background jobs.</>
                        : <>Current statement — automatic matching, settlement and journal creation may run after upload.</>}
                    </span>
                  </label>
                </div>
              </div>

              {/* Right: Drop zone */}
              <div className="lg:col-span-2 p-5">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`h-full min-h-[200px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center text-center cursor-pointer transition-all ${
                    dragOver ? 'border-success bg-success/10 scale-[1.01]'
                             : uploading ? 'border-sky-400 bg-info/10'
                             : 'border-border hover:border-emerald-400 hover:bg-success/10'
                  }`}
                  data-testid="upload-dropzone"
                >
                  <input ref={fileInputRef} type="file" accept=".pdf,.xlsx,.xls,.csv" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} data-testid="upload-file-input" />
                  {uploading ? (
                    <>
                      <Loader2 size={40} className="text-info animate-spin mb-3" />
                      <p className="text-sm font-medium text-foreground">Parsing statement…</p>
                      <p className="text-xs text-muted-foreground mt-1">This may take a few seconds for large PDFs</p>
                    </>
                  ) : (
                    <>
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 mb-3">
                        <Upload size={24} className="text-white" />
                      </div>
                      <p className="text-base font-semibold text-foreground">Drop statement here or click to browse</p>
                      <p className="text-xs text-muted-foreground mt-1">Supports PDF, Excel (.xlsx/.xls) & CSV — up to 20MB</p>
                      <div className="flex gap-1.5 mt-3">
                        <Badge variant="outline" className="text-[10px]">PDF</Badge>
                        <Badge variant="outline" className="text-[10px]">XLSX</Badge>
                        <Badge variant="outline" className="text-[10px]">CSV</Badge>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Re-scan after upload — multi-account auto-match (iter390) */}
            <div className="px-4 pb-3 -mt-1 flex items-center justify-end gap-2 border-t border-border/40 pt-3 bg-muted/20">
              <span className="text-[11px] text-muted-foreground mr-auto">
                Auto-match every bank/cash account against journal entries — runs automatically after each upload.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={autoRescan}
                disabled={rescanning}
                data-testid="rescan-after-upload-btn"
                className="h-8 text-xs gap-1 border-violet-200 text-violet-700 hover:bg-violet-50"
              >
                {rescanning ? <Loader2 size={13} className="animate-spin" /> : <Banknote size={13} />}
                Re-scan now
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* iter881 — Parse skipped rows banner + debug drawer */}
        {lastUploadResult?.skipped_count > 0 && (
          <div
            className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 flex items-center gap-3"
            data-testid="skipped-rows-banner"
          >
            <AlertTriangle size={18} className="text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                Parse skipped {lastUploadResult.skipped_count} row{lastUploadResult.skipped_count === 1 ? '' : 's'}
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300/80">
                Some rows in <span className="font-medium">{lastUploadResult.file_name}</span> could not be parsed and were dropped.
                Statement uploaded with {lastUploadResult.transactions_parsed} valid transactions.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-900 hover:bg-amber-100 gap-1"
              onClick={() => setShowSkippedDrawer(true)}
              data-testid="show-skipped-rows-btn"
            >
              <Eye size={13} /> Show skipped rows
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-amber-700 hover:bg-amber-100 h-8 w-8 p-0"
              onClick={() => setLastUploadResult(null)}
              title="Dismiss"
              data-testid="dismiss-skipped-banner-btn"
            >
              <X size={14} />
            </Button>
          </div>
        )}

        {/* iter920 — Reconciliation summary from the on-upload auto-reconcile
            (reuses the upload response; no extra call, no second engine). */}
        {lastUploadResult?.auto_reconcile?.ran && (() => {
          const c = lastUploadResult.auto_reconcile.counts || {};
          const auto = c.auto_reconciled || 0;
          const confirm = c.needs_confirm || 0;
          const review = c.review || 0;
          const skipped = (c.already_reconciled || 0) + (c.backfilled || 0);
          const items = [
            auto > 0 && { key: 'auto', label: 'Auto-reconciled', value: auto, cls: 'text-emerald-700' },
            confirm > 0 && { key: 'confirm', label: 'Needs confirmation', value: confirm, cls: 'text-amber-700' },
            review > 0 && { key: 'review', label: 'Needs review', value: review, cls: 'text-slate-700' },
            skipped > 0 && { key: 'skipped', label: 'Already reconciled / skipped', value: skipped, cls: 'text-sky-700' },
          ].filter(Boolean);
          return (
            <div
              className="rounded-xl border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20 p-4"
              data-testid="recon-upload-summary"
            >
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="mt-0.5 shrink-0 text-indigo-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">Bank statement imported</p>
                  {items.length === 0 ? (
                    <p className="mt-0.5 text-xs text-indigo-800/80">All transactions were already reconciled — nothing new to process.</p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
                      {items.map((it) => (
                        <div key={it.key} className="flex items-baseline gap-1.5" data-testid={`recon-upload-${it.key}`}>
                          <span className={`text-lg font-bold ${it.cls}`}>{it.value}</span>
                          <span className="text-xs text-slate-600">{it.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" asChild className="bg-indigo-600 hover:bg-indigo-700" data-testid="recon-upload-view-inbox-btn">
                      <Link to={`/reconciliation-inbox?stmt=${lastUploadResult.id}`}>View Reconciliation Inbox</Link>
                    </Button>
                    {(confirm > 0 || review > 0) && (
                      <Button size="sm" variant="outline" asChild className="border-indigo-300 text-indigo-800" data-testid="recon-upload-view-review-btn">
                        <Link to={`/reconciliation-inbox?stmt=${lastUploadResult.id}&filter=review`}>View Review Items ({confirm + review})</Link>
                      </Button>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-indigo-700 hover:bg-indigo-100"
                  onClick={() => setLastUploadResult({ ...lastUploadResult, auto_reconcile: { ...lastUploadResult.auto_reconcile, ran: false } })}
                  title="Dismiss"
                  data-testid="dismiss-recon-summary-btn"
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          );
        })()}
        <OrphanInboxCard
          orphans={orphans}
          expanded={orphansExpanded}
          busy={orphansBusy}
          onToggleExpand={() => setOrphansExpanded(!orphansExpanded)}
          onRebind={orphanRebind}
          onRebindAllMatching={orphanRebindAllMatching}
          onDetach={orphanDetach}
        />

        {/* iter710 — One-click maintenance: fix wrong-month grouping on
            legacy uploads by backfilling period_start / period_end from
            txn dates. Dry-runs first so the admin always sees a preview.
            Hidden once there's nothing left to re-stamp. */}
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="restamp-legacy-card">
          <div className="text-xs">
            <p className="font-bold text-amber-900">Re-stamp legacy statements</p>
            <p className="text-amber-800/80 mt-0.5 leading-relaxed">
              Older uploads predate the new month-grouping fix. Click to fill in <code className="px-1 py-0.5 rounded bg-amber-100">period_start</code> / <code className="px-1 py-0.5 rounded bg-amber-100">period_end</code> from each statement&apos;s txn dates. Idempotent · existing data untouched · always previews first.
            </p>
          </div>
          <button
            type="button"
            onClick={restampLegacyPeriods}
            disabled={restampBusy}
            className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
            data-testid="restamp-legacy-btn"
          >
            {restampBusy ? 'Working…' : 'Re-stamp legacy statements'}
          </button>
        </div>

        {/* iter540 — Monthly Reconciliation Tracker extracted to component (iter566) */}
        <MonthlyReconciliationCard
          monthly={monthly}
          expandedYm={monthlyExpanded}
          onToggle={setMonthlyExpanded}
          onOpenStatement={viewDetail}
          fmt0={fmt0}
          onLockChanged={fetchMonthlyCompletion}
        />

        {/* iter541 — Auto-booked entries card extracted to component (iter566) */}
        <AutoBookedEntriesCard
          entries={autoBookedEntries}
          expanded={autoBookedExpanded}
          selected={autoBookedSelected}
          onToggleExpand={() => setAutoBookedExpanded((v) => !v)}
          onSelectAll={(checked) => setAutoBookedSelected(checked ? new Set(autoBookedEntries.map((x) => x.marker_id)) : new Set())}
          onSelectOne={(markerId, checked) => {
            const next = new Set(autoBookedSelected);
            if (checked) next.add(markerId); else next.delete(markerId);
            setAutoBookedSelected(next);
          }}
          onUndoOne={undoAutoBookedEntry}
          onBulkUndo={bulkUndoSelected}
          onExportCsv={exportAutoBookedCsv}
        />

        {/* Statements List */}
        <Card className="border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2">
                Uploaded statements
                <Badge variant="secondary" className="font-normal">{statements.length}</Badge>
              </span>
              {/* iter517 — Boost ALL button. Only renders when there are >=1 sub-95% statements. */}
              {(() => {
                const eligible = Object.values(healthSummary).filter(h => h.score < 95);
                if (eligible.length === 0) return null;
                const avgScore = Math.round(eligible.reduce((a, h) => a + h.score, 0) / eligible.length);
                return (
                  <button
                    type="button"
                    onClick={async () => {
                      const msg = `Boost ALL ${eligible.length} statement${eligible.length === 1 ? '' : 's'} below 95% health (current avg: ${avgScore}%)?\n\nFor each one this will:\n  • Auto-create suppliers from unmatched inbound rows\n  • Link bank rows to matching suppliers\n  • Re-run auto-match\n\nLargest statements (1000+ txns) may take 30-60s each. Continue?`;
                      if (!window.confirm(msg)) return;
                      const tid = toast.loading(`Boosting ${eligible.length} statement${eligible.length === 1 ? '' : 's'}…`);
                      try {
                        const r = await api.post('/bank-statements/boost-all-health', { score_below: 95, max_statements: 50 });
                        toast.dismiss(tid);
                        const d = r.data || {};
                        if (d.processed > 0) {
                          toast.success(`Boosted ${d.processed} statement${d.processed === 1 ? '' : 's'} · avg +${d.average_score_delta} pts · ${d.total_suppliers_created} suppliers + ${d.total_txn_links_added} links`);
                        } else {
                          toast.info('Nothing to boost — all statements already excellent.');
                        }
                        fetchData();
                      } catch (e) {
                        toast.dismiss(tid);
                        toast.error(e.response?.data?.detail || 'Boost All failed');
                      }
                    }}
                    data-testid="boost-all-statements-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 transition-all shadow-sm hover:shadow"
                    title={`Boost ${eligible.length} statement${eligible.length === 1 ? '' : 's'} currently below 95% health`}
                  >
                    <Sparkles size={12} /> Boost ALL ({eligible.length})
                  </button>
                );
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {/* iter926 — "Open items only" quick filter. Reuses reconStatusMap
                + shared statementStatus; a statement is "open" when its status
                is 'partial' (open_count > 0). No new status/count logic. */}
            {(() => {
              const openCount = statements.filter((s) => reconStatusMap[s.id] && statementStatus(reconStatusMap[s.id]).key === 'partial').length;
              return (
                <div className="mb-3 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setOpenItemsOnly((v) => !v)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      openItemsOnly
                        ? 'border-amber-300 bg-amber-50 text-amber-700'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted/50'
                    }`}
                    data-testid="open-items-only-toggle"
                    data-active={openItemsOnly}
                    title="Show only statements that still have transactions needing reconciliation"
                  >
                    <span className={`h-2 w-2 rounded-full ${openItemsOnly ? 'bg-amber-500' : 'bg-muted-foreground'}`}></span>
                    Open items only
                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" data-testid="open-items-count">{openCount}</span>
                  </button>
                </div>
              );
            })()}
            {statements.length === 0 ? (
              <div className="text-center py-10">
                <FileText size={36} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No statements yet</p>
                <p className="text-xs text-muted-foreground mt-1">Upload a bank statement above to get started</p>
              </div>
            ) : (() => {
              // iter540 — Group statements by year-month so the user can
              // collapse older months. The most-recent 2 months stay
              // open by default; older ones collapse on first render.
              // iter709 — Use the new `period_start` field (set by the
              // backend from sorted min txn date) so a statement whose
              // bank emitted txns DESC doesn't land in the wrong month.
              // Falls back to a defensive parse of the legacy `period`
              // string (extracts the earliest YYYY-MM from it).
              const getYm = (s) => {
                if (s.period_start && s.period_start.length >= 7) {
                  return s.period_start.slice(0, 7);
                }
                const p = (s.period || '').trim();
                if (p) {
                  // Pull every YYYY-MM in the string and keep the earliest.
                  const matches = p.match(/\d{4}-\d{2}/g) || [];
                  if (matches.length) {
                    matches.sort();
                    return matches[0];
                  }
                }
                // Last resort: upload date (will be wrong if user
                // re-uploads a stale statement later, but stays
                // chronologically near reality).
                const u = s.uploaded_at || s.created_at || '';
                if (u && u.length >= 7) return u.slice(0, 7);
                return 'unknown';
              };
              const groups = {};
              const _visible = openItemsOnly
                ? statements.filter((s) => reconStatusMap[s.id] && statementStatus(reconStatusMap[s.id]).key === 'partial')
                : statements;
              for (const s of _visible) {
                const ym = getYm(s);
                if (!groups[ym]) groups[ym] = [];
                groups[ym].push(s);
              }
              const sortedYms = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));
              if (openItemsOnly && _visible.length === 0) {
                return (
                  <div className="text-center py-10" data-testid="open-items-empty">
                    <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2" />
                    <p className="text-sm text-muted-foreground">No statements with open items — everything is reconciled. 🎉</p>
                  </div>
                );
              }
              const isOpen = (ym, idx) => {
                if (Object.prototype.hasOwnProperty.call(stmtGroupOpen, ym)) return stmtGroupOpen[ym];
                return idx < 2 || ym === 'unknown';  // default open: 2 most recent
              };
              return (
                <div className="space-y-3" data-testid="statements-grouped">
                  {sortedYms.map((ym, idx) => {
                    const list = groups[ym];
                    const open = isOpen(ym, idx);
                    const [yy, mm] = ym.split('-');
                    const label = (yy && mm && ym !== 'unknown')
                      ? new Date(parseInt(yy), parseInt(mm) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
                      : 'Undated';
                    const totalIn = list.reduce((a, s) => a + (s.total_credit || 0), 0);
                    const totalOut = list.reduce((a, s) => a + (s.total_debit || 0), 0);
                    // Cross-reference with monthly tracker if available
                    const mInfo = monthly?.months?.find(m => m.ym === ym);
                    const pct = mInfo ? Math.round(mInfo.completion_pct || 0) : null;
                    const status = mInfo?.status;
                    const statusDot = status === 'closed' ? 'bg-emerald-500' : (status === 'near_closed' || status === 'nearly_closed') ? 'bg-sky-500' : status === 'in_progress' ? 'bg-orange-500' : status === 'open' ? 'bg-amber-500' : 'bg-muted-foreground';
                    return (
                      <div key={ym} className="border border-border rounded-xl overflow-hidden" data-testid={`stmt-group-${ym}`}>
                        <button
                          type="button"
                          onClick={() => setStmtGroupOpen({ ...stmtGroupOpen, [ym]: !open })}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                          data-testid={`stmt-group-toggle-${ym}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {open ? <ChevronDown size={14} className="text-muted-foreground shrink-0" /> : <ChevronRight size={14} className="text-muted-foreground shrink-0" />}
                            <span className="text-sm font-semibold text-foreground">{label}</span>
                            <Badge variant="secondary" className="text-[10px] font-normal">{list.length}</Badge>
                            {pct !== null && (
                              <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                                <span className={`h-1.5 w-1.5 rounded-full ${statusDot}`} />
                                <span className="tabular-nums">{pct}%</span>
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-[11px] tabular-nums shrink-0">
                            <span className="text-success">↑ {fmt0(totalIn)}</span>
                            <span className="text-error">↓ {fmt0(totalOut)}</span>
                          </div>
                        </button>
                        {open && (
                          <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            {list.map(s => {
                              const active = detail?.id === s.id;
                              const net = (s.total_credit || 0) - (s.total_debit || 0);
                              const health = healthSummary[s.id];
                              const healthClasses = health ? ({
                                emerald: { badge: 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100', dot: 'bg-emerald-500' },
                                sky:     { badge: 'bg-sky-100 text-sky-800 border-sky-300 hover:bg-sky-100',                 dot: 'bg-sky-500' },
                                amber:   { badge: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100',         dot: 'bg-amber-500' },
                                rose:    { badge: 'bg-rose-100 text-rose-800 border-rose-300 hover:bg-rose-100',             dot: 'bg-rose-500' },
                              })[health.score >= 95 ? 'emerald' : health.score >= 80 ? 'sky' : health.score >= 60 ? 'amber' : 'rose'] : null;
                              return (
                                <div
                                  key={s.id}
                                  onClick={() => viewDetail(s.id)}
                                  className={`group relative flex flex-col p-4 rounded-xl border cursor-pointer transition-all ${
                                    active
                                      ? 'border-emerald-400 bg-success/10 shadow-md shadow-emerald-100'
                                      : 'border-border hover:border-success/30 hover:bg-muted/50'
                                  }`}
                                  data-testid={`statement-card-${s.id}`}
                                >
                                  <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <Badge className="bg-info/20 text-info text-[10px] font-semibold">{s.bank_name || 'Unknown'}</Badge>
                                        {s.branch_id && <Badge variant="outline" className="text-[10px]">{branchName(s.branch_id)}</Badge>}
                                        {s.bank_account_id && (() => {
                                          const acc = bankAccounts.find(a => a.id === s.bank_account_id);
                                          return <Badge className="bg-success/20 text-success text-[10px] gap-0.5"><Link2 size={9} />{acc ? acc.name : 'Linked'}</Badge>;
                                        })()}
                                        {active && <Badge className="bg-success text-white text-[10px]">Viewing</Badge>}
                                        {health && healthClasses && (
                                          <Badge
                                            className={`text-[10px] font-bold gap-1 border ${healthClasses.badge}`}
                                            data-testid={`stmt-health-${s.id}`}
                                            title={`Reconciliation Health: ${health.matched}/${health.total_txns} matched${health.recommendations?.length ? ' · ' + health.recommendations[0] : ''}`}
                                          >
                                            <span className={`h-1.5 w-1.5 rounded-full ${healthClasses.dot}`}></span>
                                            {health.score}% health
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-sm font-medium text-foreground truncate flex items-center gap-1.5" title={s.file_name}>
                                        {s.review_status === 'done' && (
                                          <CheckCircle2
                                            size={13}
                                            className="text-emerald-600 shrink-0"
                                            title={`Reviewed${s.reviewed_by ? ' by ' + s.reviewed_by : ''}${s.reviewed_at ? ' on ' + s.reviewed_at.slice(0,10) : ''}`}
                                            data-testid={`stmt-reviewed-badge-${s.id}`}
                                          />
                                        )}
                                        <span className="truncate">{s.file_name}</span>
                                      </div>
                                      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                        <span>{s.period} · {s.transaction_count} txns</span>
                                        {reconStatusMap[s.id] && (() => {
                                          const rst = statementStatus(reconStatusMap[s.id]);
                                          return (
                                            <span
                                              className="inline-flex items-center gap-1"
                                              data-testid={`stmt-recon-status-${s.id}`}
                                              data-status={rst.key}
                                              title={`Reconciliation: ${rst.label}`}
                                            >
                                              <span className={`h-1.5 w-1.5 rounded-full ${rst.dot}`}></span>
                                              <span className={rst.textCls}>{rst.label}</span>
                                            </span>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                      <Select
                                        value={s.bank_account_id || 'none'}
                                        onValueChange={(v) => linkStatementToAccount(s.id, v === 'none' ? null : v)}
                                      >
                                        <SelectTrigger className="h-7 w-7 p-0 border-0 bg-transparent hover:bg-muted rounded-lg [&>svg]:hidden" data-testid={`link-statement-${s.id}`}>
                                          <Link2 size={14} className={s.bank_account_id ? 'text-success mx-auto' : 'text-muted-foreground mx-auto'} />
                                        </SelectTrigger>
                                        <SelectContent align="end">
                                          <div className="text-[10px] text-muted-foreground px-2 py-1 uppercase tracking-wider">Link to bank account</div>
                                          <SelectItem value="none">
                                            <span className="flex items-center gap-1.5 text-muted-foreground"><Link2Off size={12} /> Unlinked</span>
                                          </SelectItem>
                                          {bankAccounts.map(a => (
                                            <SelectItem key={a.id} value={a.id}>
                                              <span className="flex items-center gap-1.5">
                                                <Building2 size={12} className="text-info" />
                                                {a.name} <span className="text-muted-foreground">— {a.bank_name}</span>
                                              </span>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); viewDetail(s.id); }} data-testid={`view-statement-${s.id}`}><Eye size={14} /></Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={async (e) => { e.stopPropagation(); if (window.confirm('Delete this statement?')) { await api.delete(`/bank-statements/${s.id}`); fetchData(); if (active) { setDetail(null); setAnalysis(null); setReconciliation(null); } } }} data-testid={`delete-statement-${s.id}`}><Trash2 size={14} /></Button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-auto">
                                    <div className="text-center p-1.5 rounded-lg bg-success/10">
                                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">In</div>
                                      <div className="text-xs font-bold text-success">{fmt0(s.total_credit)}</div>
                                    </div>
                                    <div className="text-center p-1.5 rounded-lg bg-error/10">
                                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Out</div>
                                      <div className="text-xs font-bold text-error">{fmt0(s.total_debit)}</div>
                                    </div>
                                    <div className={`text-center p-1.5 rounded-lg ${net >= 0 ? 'bg-primary/10' : 'bg-warning/10'}`}>
                                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Net</div>
                                      <div className={`text-xs font-bold ${net >= 0 ? 'text-primary' : 'text-warning'}`}>{fmt0(net)}</div>
                                    </div>
                                  </div>
                                  {health && health.score < 95 && (
                                    <button
                                      type="button"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const recs = (health.recommendations || []).join(' · ') || 'Auto-create suppliers + re-run auto-match';
                                        if (!window.confirm(`Boost the Reconciliation Health of this statement?\n\n${recs}\n\nThis will:\n  • Auto-create suppliers from every unmatched inbound row\n  • Re-run auto-match against journal/sales/expenses\n  • Show you the before/after score`)) return;
                                        try {
                                          const r = await api.post(`/bank-statements/${s.id}/boost-health`, {});
                                          const beforeS = r.data?.before?.score ?? 0;
                                          const afterS = r.data?.after?.score ?? 0;
                                          const delta = afterS - beforeS;
                                          const sc = r.data?.deltas?.suppliers_created || 0;
                                          const ma = r.data?.deltas?.matches_added || 0;
                                          if (delta > 0) {
                                            toast.success(`Boosted: ${beforeS}% → ${afterS}% (+${delta} pts) · ${sc} supplier${sc === 1 ? '' : 's'} + ${ma} match${ma === 1 ? '' : 'es'}`);
                                          } else if (sc > 0 || ma > 0) {
                                            toast.info(`Created ${sc} supplier${sc === 1 ? '' : 's'} + ${ma} match${ma === 1 ? '' : 'es'} — score unchanged (${afterS}%)`);
                                          } else {
                                            toast.info('Nothing to boost — all auto-fixable rows already linked.');
                                          }
                                          fetchData();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || 'Boost failed');
                                        }
                                      }}
                                      data-testid={`stmt-boost-health-${s.id}`}
                                      className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 transition-all shadow-sm hover:shadow"
                                      title={(health.recommendations || []).join(' · ')}
                                    >
                                      <Sparkles size={11} /> Boost health · {health.score}% → est. {Math.min(100, health.score + Math.min(40, health.unmatched_inbound + Math.floor(health.unmatched_outbound / 2)))}%
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Detail View */}
        {detail && (
          <Card id="statement-detail" className="border-border border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="font-outfit flex items-center gap-2 flex-wrap">
                  <Badge className="bg-info/20 text-info">{detail.bank_name}</Badge>
                  {detail.bank_account_id && (() => {
                    const acc = bankAccounts.find(a => a.id === detail.bank_account_id);
                    return <Badge className="bg-success/20 text-success gap-1"><Link2 size={11} />{acc ? acc.name : 'Linked account'}</Badge>;
                  })()}
                  <span className="text-base">{detail.transaction_count} transactions · {detail.period}</span>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select
                    value={detail.bank_account_id || 'none'}
                    onValueChange={(v) => linkStatementToAccount(detail.id, v === 'none' ? null : v)}
                  >
                    <SelectTrigger className="h-8 rounded-xl w-56" data-testid="detail-link-select">
                      <SelectValue placeholder="Link to bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="flex items-center gap-1.5 text-muted-foreground"><Link2Off size={12} /> Unlinked</span>
                      </SelectItem>
                      {bankAccounts.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          <span className="flex items-center gap-1.5">
                            <Building2 size={12} className="text-info" />
                            {a.name} — {a.bank_name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* iter485 — Mark / Un-mark statement as reviewed */}
                  <Button
                    size="sm"
                    variant={detail.review_status === 'done' ? 'default' : 'outline'}
                    className={detail.review_status === 'done' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
                    onClick={() => toggleReviewed(detail.id, detail.review_status)}
                    title={detail.review_status === 'done'
                      ? `Reviewed by ${detail.reviewed_by || 'user'}${detail.reviewed_at ? ' on ' + detail.reviewed_at.slice(0, 10) : ''} — click to re-open`
                      : 'Mark this statement as fully reviewed (codes labelled, rows linked, reconciled)'}
                    data-testid="stmt-review-toggle"
                  >
                    <CheckCircle2 size={14} className="mr-1" />
                    {detail.review_status === 'done' ? 'Reviewed' : 'Mark reviewed'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => {
                    setDetail(null); setAnalysis(null); setReconciliation(null);
                    try {
                      const next = new URLSearchParams(window.location.search);
                      next.delete('statement');
                      next.delete('recon');
                      setSearchParams(next, { replace: true });
                    } catch { /* noop */ }
                  }}><X size={14} className="mr-1" /> Close</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* iter835 — Persistent inline banner: advances the system
                  auto-settled from this statement's transactions. Backed
                  by `bank_statements.advance_recon` (populated on upload
                  when the recon engine linked anything). Only renders
                  when at least one link exists. */}
              {detail.advance_recon && detail.advance_recon.linked_count > 0 && (
                <div
                  className="mb-4 rounded-xl border-2 border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3"
                  data-testid="advance-recon-banner"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
                      <Link2 size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-emerald-900 dark:text-emerald-100">
                            {detail.advance_recon.linked_count} pass-through advance{detail.advance_recon.linked_count === 1 ? '' : 's'} auto-settled
                            <span className="ml-2 tabular-nums font-semibold">
                              {Number(detail.advance_recon.total_amount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="text-[11px] text-emerald-800/80 dark:text-emerald-200/70 mt-0.5">
                            Auto-linked when this statement was uploaded{detail.advance_recon.ran_at ? ` · ${String(detail.advance_recon.ran_at).slice(0, 10)}` : ''}. Review or unlink each below.
                          </div>
                        </div>
                        {/* iter838 — Bulk "Unlink all" escape hatch when
                            an entire statement was mis-matched. */}
                        {detail.advance_recon.linked_count > 1 && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm(`Reverse ALL ${detail.advance_recon.linked_count} auto-matches on this statement? Each advance goes back to open and each transaction is un-flagged.`)) return;
                              try {
                                const res = await api.post(`/bank-statements/${detail.id}/unlink-all-advances`);
                                toast.success(`Unlinked ${res.data?.unlinked_count || 0} advance${(res.data?.unlinked_count || 0) === 1 ? '' : 's'} — all advances are open again`);
                                viewDetail(detail.id);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to unlink all');
                              }
                            }}
                            className="text-[10px] px-2 py-1 rounded border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40 hover:text-red-800 font-medium shrink-0"
                            title="Reverse every auto-match on this statement in one shot"
                            data-testid="advance-recon-unlink-all"
                          >
                            Unlink all
                          </button>
                        )}
                      </div>
                      {Array.isArray(detail.advance_recon.linked) && detail.advance_recon.linked.length > 0 && (
                        <ul className="mt-2 space-y-0.5 text-[11px] text-emerald-900 dark:text-emerald-100" data-testid="advance-recon-list">
                          {detail.advance_recon.linked.slice(0, 8).map((l, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-emerald-600 shrink-0" />
                              <span className="font-medium truncate">{l.person_name || 'Unknown'}</span>
                              <span className="text-emerald-700/70 dark:text-emerald-300/70">·</span>
                              <span className="capitalize text-emerald-700/70 dark:text-emerald-300/70">{l.person_type || ''}</span>
                              <span className="ml-auto tabular-nums font-semibold">{Number(l.amount || 0).toFixed(2)}</span>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm(`Unlink ${l.person_name || 'this person'} (${Number(l.amount || 0).toFixed(2)}) from the advance?`)) return;
                                  try {
                                    await api.post(`/pass-through-advances/${l.advance_id}/unlink`, {
                                      collection: l.collection, row_id: l.row_id,
                                    });
                                    toast.success('Unlinked — the advance is open again');
                                    viewDetail(detail.id);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Failed to unlink');
                                  }
                                }}
                                className="ml-1 text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:text-emerald-900 text-emerald-800 dark:text-emerald-200"
                                title="Reverse this auto-match — the advance goes back to open and the transaction is un-flagged"
                                data-testid={`advance-recon-unlink-${i}`}
                              >
                                Unlink
                              </button>
                            </li>
                          ))}
                          {detail.advance_recon.linked.length > 8 && (
                            <li className="text-[10px] text-emerald-700/70 dark:text-emerald-300/70 pl-3">
                              +{detail.advance_recon.linked.length - 8} more…
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <Tabs defaultValue="overview">
                <TabsList className="flex-wrap bg-muted/70 p-1 rounded-xl">
                  <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="smart" data-testid="tab-smart-groups">Smart Groups</TabsTrigger>
                  <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">Reconciliation</TabsTrigger>
                  <TabsTrigger value="pos" data-testid="tab-pos">POS Machines</TabsTrigger>
                  <TabsTrigger value="parties" data-testid="tab-parties">Parties</TabsTrigger>
                  <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
                  <TabsTrigger value="notes" data-testid="tab-notes" className="relative">
                    Notes
                    {monthEndNotes.length > 0 && (
                      <Badge variant="secondary" className="ml-1 h-4 px-1 text-[9px] font-bold" data-testid="notes-count-badge">
                        {monthEndNotes.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* OVERVIEW — summary stats + categories pie + daily */}
                <TabsContent value="overview" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="p-3 bg-success/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">Total In</div><div className="text-lg font-bold text-success">{fmt0(detail.total_credit)}</div></div>
                    <div className="p-3 bg-error/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">Total Out</div><div className="text-lg font-bold text-error">{fmt0(detail.total_debit)}</div></div>
                    <div className="p-3 bg-primary/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">Net</div><div className={`text-lg font-bold ${(detail.total_credit - detail.total_debit) >= 0 ? 'text-success' : 'text-error'}`}>{fmt0(detail.total_credit - detail.total_debit)}</div></div>
                    <div className="p-3 bg-warning/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">Bank Fees</div><div className="text-lg font-bold text-warning">{fmt0(analysis?.total_bank_fees || 0)}</div></div>
                    <div className="p-3 bg-info/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">POS Sales</div><div className="text-lg font-bold text-info">{fmt0(analysis?.total_pos_sales || 0)}</div></div>
                  </div>

                  {detail.categories && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-semibold mb-2 text-foreground">By category</div>
                        {Object.entries(detail.categories).map(([cat, data], i) => {
                          // iter532 — show "+ Book as Expense" button for fee categories
                          const fcat = feeStatus?.categories?.[cat];
                          const monthsBooked = fcat ? Object.values(fcat.months || {}).filter(m => m.booked).length : 0;
                          const monthsTotal = fcat ? Object.keys(fcat.months || {}).length : 0;
                          const allBooked = fcat && monthsTotal > 0 && monthsBooked === monthsTotal;
                          const someUnbooked = fcat && monthsTotal > 0 && monthsBooked < monthsTotal;
                          return (
                            <div key={cat} className="flex justify-between items-center p-2.5 border-b border-border">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                                <span className="text-sm font-medium">{CAT_LABELS[cat] || cat}</span>
                                <Badge variant="secondary" className="text-[10px]">{data.count}</Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-right text-xs">
                                  <span className="text-success mr-3">+{fmt0(data.credit)}</span>
                                  <span className="text-error">-{fmt0(data.debit)}</span>
                                </div>
                                {fcat && monthsTotal > 0 && (
                                  allBooked ? (
                                    <Badge
                                      variant="secondary"
                                      className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"
                                      title={`Already added to Expenses. Months: ${Object.keys(fcat.months).join(', ')}. Re-uploading this statement won't double-book.`}
                                      data-testid={`fee-booked-${cat}`}
                                    >
                                      <CheckCircle2 size={10} className="mr-0.5" /> Added to Expenses
                                    </Badge>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-[10px] rounded-lg text-orange-600 border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/40"
                                      disabled={feeBookingLoading === cat}
                                      onClick={() => bookFeeCategory(cat)}
                                      data-testid={`fee-book-${cat}`}
                                      title={someUnbooked ? `${monthsBooked}/${monthsTotal} months already in Expenses. Click to add the remaining ${monthsTotal - monthsBooked}.` : 'Add this category as one Expense per month (last day of month)'}
                                    >
                                      {feeBookingLoading === cat ? <Loader2 size={10} className="mr-1 animate-spin" /> : <Plus size={10} className="mr-0.5" />}
                                      {someUnbooked
                                        ? `Add ${monthsTotal - monthsBooked} month${monthsTotal - monthsBooked === 1 ? '' : 's'} to Expenses`
                                        : `Add ${monthsTotal} month${monthsTotal === 1 ? '' : 's'} to Expenses`}
                                    </Button>
                                  )
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        <div className="text-sm font-semibold mb-2 text-foreground">Category breakdown</div>
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie
                              data={Object.entries(detail.categories).filter(([, d]) => d.credit > 0 || d.debit > 0).map(([k, v]) => ({ name: CAT_LABELS[k] || k, value: v.credit + v.debit }))}
                              cx="50%" cy="50%" outerRadius={90} dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {Object.keys(detail.categories).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => fmt(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {detail.daily_summary?.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold mt-4 mb-2 text-foreground">Daily movement</div>
                      <div className="overflow-x-auto border rounded-xl">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr className="border-b">
                              <th className="text-left p-2 text-xs font-medium">Date</th>
                              <th className="text-right p-2 text-xs font-medium">In</th>
                              <th className="text-right p-2 text-xs font-medium">Out</th>
                              <th className="text-right p-2 text-xs font-medium">Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.daily_summary.map(d => (
                              <tr key={d.date} className="border-b hover:bg-muted/50">
                                <td className="p-2 text-sm">{d.date}</td>
                                <td className="p-2 text-sm text-right text-success">{fmt(d.credit)}</td>
                                <td className="p-2 text-sm text-right text-error">{fmt(d.debit)}</td>
                                <td className={`p-2 text-sm text-right font-bold ${d.credit - d.debit >= 0 ? 'text-success' : 'text-error'}`}>{fmt(d.credit - d.debit)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* SMART GROUPS — iter496 */}
                <TabsContent value="smart" className="space-y-3 mt-4" data-testid="smart-groups-content">
                  <SmartGroupsTab
                    statementId={detail.id}
                    branchId={detail.branch_id}
                    branches={branches}
                    onChange={() => viewDetail(detail.id)}
                  />
                </TabsContent>


                {/* RECONCILIATION (iter570 — extracted) */}
                <TabsContent value="reconciliation" className="space-y-4 mt-4" data-testid="reconciliation-content">
                  <ReconciliationTab
                    statementId={detail.id}
                    statementPeriod={detail.period}
                    reconciliation={reconciliation}
                    reconLoading={reconLoading}
                    reconStatusFilter={reconStatusFilter}
                    setReconStatusFilter={setReconStatusFilterPersisted}
                    fmt={fmt}
                    fmt0={fmt0}
                    onNoteSaved={loadMonthEndNotes}
                  />
                </TabsContent>


                {/* POS MACHINES (breakdown + mismatches) */}
                <TabsContent value="pos" className="space-y-4 mt-4">
                  <p className="text-sm text-muted-foreground">POS machines with sales (MADA/VISA/MC), fees, VAT breakdown</p>
                  {analysis?.pos_by_machine && Object.keys(analysis.pos_by_machine).length > 0 ? (
                    Object.entries(analysis.pos_by_machine).sort((a, b) => b[1].sales_total - a[1].sales_total).map(([mid, data]) => (
                      <div key={mid} className="border rounded-xl p-4 hover:bg-muted/50 transition-all">
                        <div className="flex justify-between items-start mb-3">
                          <div><span className="font-mono text-sm font-bold">{mid}</span><Badge className="ml-2 bg-primary/20 text-primary">{data.branch}</Badge></div>
                          <div className="text-right"><div className="text-lg font-bold text-success">{fmt(data.sales_total)}</div><div className="text-xs text-muted-foreground">{data.sales_count} sales · Net: {fmt(data.net)}</div></div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                          <div className="p-2 bg-success/10 rounded-lg text-center"><div className="text-xs text-muted-foreground">MADA</div><div className="text-sm font-bold text-success">{fmt0(data.mada)}</div>{data.mada_fee > 0 && <div className="text-xs text-error">Fee: -{data.mada_fee.toFixed(2)}</div>}</div>
                          <div className="p-2 bg-info/10 rounded-lg text-center"><div className="text-xs text-muted-foreground">VISA</div><div className="text-sm font-bold text-info">{fmt0(data.visa)}</div>{data.visa_fee > 0 && <div className="text-xs text-error">Fee: -{data.visa_fee.toFixed(2)}</div>}</div>
                          <div className="p-2 bg-orange-50 rounded-lg text-center"><div className="text-xs text-muted-foreground">MasterCard</div><div className="text-sm font-bold text-orange-700">{fmt0(data.mastercard)}</div>{data.mc_fee > 0 && <div className="text-xs text-error">Fee: -{data.mc_fee.toFixed(2)}</div>}</div>
                        </div>
                        <div className="flex flex-wrap gap-4 text-xs items-center">
                          <span className="text-success">Total In: {fmt(data.sales_total)}</span>
                          <span className="text-error">Fees: -{fmt(data.fees)}</span>
                          <span className="text-error">VAT: -{fmt(data.vat)}</span>
                          <span className="font-bold">Net: {fmt(data.net)}</span>
                          {/* iter449 — Quick action: book the VAT-on-bank-fee
                              portion as an expense routed to the new
                              "VAT on Bank Charges – Input" COA account. */}
                          {data.vat > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="ml-auto h-7 text-[10px] border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
                              onClick={() => {
                                setBankPaymentSeed({
                                  amount: data.vat,
                                  payee_type: 'expense_category',
                                  category_hint: 'vat on bank charges',
                                  description: `VAT on bank fees — POS ${mid} (${data.branch})`,
                                });
                                setBankPaymentOpen(true);
                              }}
                              data-testid={`pos-pay-vat-${mid}`}
                              title="Book the POS VAT portion as an expense to VAT on Bank Charges – Input">
                              <Wand2 size={11} className="mr-1" /> Pay VAT on bank fees
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : <p className="text-center text-muted-foreground py-8 text-sm">No POS machines detected</p>}

                  {/* Mismatches */}
                  {analysis?.mismatches?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-sm font-semibold mb-2 text-foreground flex items-center gap-2">
                        <AlertTriangle size={14} className="text-warning" /> Mismatches vs Smarto sales
                      </div>
                      <div className="space-y-2">
                        {analysis.mismatches.map((m, i) => (
                          <div key={i} className={`p-3 border rounded-xl ${Math.abs(m.difference) > 100 ? 'bg-error/5 border-error/30' : 'bg-warning/5 border-warning/30'}`}>
                            <div className="flex justify-between items-center">
                              <div><div className="font-bold text-sm">{m.branch}</div><div className="text-xs text-muted-foreground mt-0.5">Bank POS: {fmt(m.bank_amount)} · System: {fmt(m.system_amount)}</div></div>
                              <Badge className={Math.abs(m.difference) > 100 ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'}>Diff: {fmt(m.difference)}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* PARTIES (senders/receivers + suppliers) */}
                <TabsContent value="parties" className="space-y-4 mt-4" data-testid="parties-content">
                  <PartiesTab
                    detail={detail}
                    analysis={analysis}
                    partiesFilter={partiesFilter}
                    setPartiesFilter={setPartiesFilter}
                    partyMatchFilter={partyMatchFilter}
                    setPartyMatchFilter={setPartyMatchFilter}
                    fmt={fmt}
                    fmt0={fmt0}
                    onChanged={() => detail?.id && viewDetail(detail.id)}
                    onOpenDict={({ code, label }) => setDictState({ open: true, code, label })}
                    onOpenMatch={(sender) => { setMatchRow(sender); setMatchSupplierId(''); setMatchEntityType('supplier'); }}
                  />
                </TabsContent>

                {/* TRANSACTIONS */}
                <TabsContent value="transactions" className="mt-4" data-testid="transactions-content">
                  <TransactionsTab
                    detail={detail}
                    onChanged={() => detail?.id && viewDetail(detail.id)}
                    onOpenDict={({ code, label }) => setDictState({ open: true, code, label })}
                    onOpenLink={(txn) => {
                      setLinkTxn(txn);
                      setLinkEntityType(txn.link_type || 'supplier');
                      setLinkEntityId(txn.link_id || '');
                      setLinkEntitySearch('');
                    }}
                    onFindMatch={(idx) => setMatchDialog({ open: true, txnIdx: idx })}
                  />
                </TabsContent>

                {/* iter567 — Month-End Notes tab: persisted AI narratives
                    + any manual notes for the auditor / accountant. */}
                <TabsContent value="notes" className="mt-4 space-y-3" data-testid="notes-content">
                  <NotesTab notes={monthEndNotes} onDelete={deleteMonthEndNote} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Match-party → Supplier / Customer / Employee / Partner dialog */}
        <Dialog open={!!matchRow} onOpenChange={(open) => { if (!open) { setMatchRow(null); setMatchSupplierId(''); setMatchEntityType('supplier'); } }}>
          <DialogContent data-testid="match-party-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">Match party to {matchEntityType}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="p-3 bg-muted/50 rounded-lg border border-border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Bank party</div>
                <div className="text-sm font-medium text-foreground mt-0.5">{matchRow?.name}</div>
                {matchRow?.iban?.length > 0 && <div className="text-xs font-mono text-muted-foreground">{matchRow.iban[0]}</div>}
                {matchRow?.bank?.length > 0 && <div className="text-xs text-info">{matchRow.bank[0]}</div>}
              </div>
              {/* iter461 — entity-type selector. Resetting the ID on change
                   avoids posting a customer-id with entity_type=supplier. */}
              <div>
                <Label className="text-xs text-muted-foreground">Link as</Label>
                <div className="flex gap-1.5 mt-1" data-testid="match-entity-type-row">
                  {[
                    { v: 'supplier', l: 'Supplier' },
                    { v: 'customer', l: 'Customer' },
                    { v: 'employee', l: 'Employee' },
                    { v: 'partner', l: 'Partner' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => { setMatchEntityType(opt.v); setMatchSupplierId(''); }}
                      data-testid={`match-entity-${opt.v}`}
                      className={`px-2.5 py-1 rounded-lg border text-xs transition-colors ${
                        matchEntityType === opt.v
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-background text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Pick a {matchEntityType}</Label>
                <Select value={matchSupplierId} onValueChange={setMatchSupplierId}>
                  <SelectTrigger data-testid="match-supplier-select"><SelectValue placeholder={`Select a ${matchEntityType}…`} /></SelectTrigger>
                  <SelectContent>
                    {(matchEntityType === 'supplier' ? suppliers
                      : matchEntityType === 'customer' ? customers
                      : matchEntityType === 'employee' ? employees
                      : partners
                    ).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1.5">The sender name and IBAN will be added to this {matchEntityType} as alternate identifiers and saved as a beneficiary, so future statements auto-match.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setMatchRow(null); setMatchSupplierId(''); setMatchEntityType('supplier'); }}>Cancel</Button>
                <Button onClick={saveMatchToSupplier} disabled={!matchSupplierId} data-testid="match-party-save-btn">Save match</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Per-transaction manual link dialog */}
        <Dialog open={!!linkTxn} onOpenChange={(o) => { if (!o) setLinkTxn(null); }}>
          <DialogContent className="max-w-lg" data-testid="manual-link-txn-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">Link this transaction</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="p-3 bg-muted/50 rounded-lg border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Transaction</div>
                <div className="text-sm font-medium mt-0.5 truncate" title={linkTxn?.description}>{linkTxn?.description}</div>
                {linkTxn?.link_name && (
                  <div className="text-xs mt-1">Currently linked to <span className="font-semibold">{linkTxn.link_type}: {linkTxn.link_name}</span></div>
                )}
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Link to</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {[
                    { v: 'supplier', l: 'Supplier' },
                    { v: 'customer', l: 'Customer' },
                    { v: 'employee', l: 'Employee' },
                    { v: 'partner', l: 'Partner' },
                    { v: 'expense', l: 'Expense' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => {
                        setLinkEntityType(opt.v);
                        setLinkEntityId('');
                        setLinkEntitySearch('');
                        if (opt.v === 'expense' && linkExpenses.length === 0) loadLinkExpenses();
                      }}
                      data-testid={`manual-link-type-${opt.v}`}
                      className={`px-2.5 py-1 rounded-lg border text-xs transition-colors ${
                        linkEntityType === opt.v
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-background text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Pick a {linkEntityType}</Label>
                <Input
                  placeholder={`Search ${linkEntityType}…`}
                  value={linkEntitySearch}
                  onChange={(e) => setLinkEntitySearch(e.target.value)}
                  className="mb-2"
                  data-testid="manual-link-search"
                />
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  {(() => {
                    const list = linkEntityType === 'supplier' ? suppliers
                      : linkEntityType === 'customer' ? customers
                      : linkEntityType === 'employee' ? employees
                      : linkEntityType === 'partner' ? partners
                      : linkExpenses;
                    const filtered = list
                      .filter(x => {
                        const n = (x.name || x.description || x.vendor || '').toLowerCase();
                        return !linkEntitySearch || n.includes(linkEntitySearch.toLowerCase());
                      })
                      .slice(0, 100);
                    if (filtered.length === 0) {
                      return <div className="p-3 text-xs text-muted-foreground">No matches</div>;
                    }
                    return filtered.map(x => (
                      <button
                        key={x.id}
                        type="button"
                        onClick={() => setLinkEntityId(x.id)}
                        className={`w-full text-left p-2 text-sm border-b last:border-b-0 ${linkEntityId === x.id ? 'bg-primary/10 font-medium' : 'hover:bg-muted/50'}`}
                        data-testid={`manual-link-option-${x.id}`}
                      >
                        <div className="truncate">{x.name || x.description || x.vendor || '(no name)'}</div>
                        {x.amount != null && <div className="text-[10px] text-muted-foreground">{x.amount} · {(x.date || x.created_at || '').slice(0, 10)}</div>}
                      </button>
                    ));
                  })()}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                {linkTxn?.link_id && (
                  <Button variant="outline" className="text-error" onClick={clearManualLink} data-testid="manual-link-clear-btn">
                    Clear link
                  </Button>
                )}
                <Button variant="outline" onClick={() => setLinkTxn(null)}>Cancel</Button>
                <Button onClick={saveManualLink} disabled={!linkEntityId} data-testid="manual-link-save-btn">Save link</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* POS Machine Manager */}
        <Dialog open={showPosManager} onOpenChange={setShowPosManager}>
          <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle className="font-outfit">POS Machine → Branch Mapping</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mb-3">Add POS machines manually or auto-detected from statements. Click fields to edit.</p>

            <div className="flex gap-2 items-end p-3 bg-muted/50 rounded-xl border mb-3">
              <div className="flex-1"><Label className="text-xs">Machine ID</Label><Input id="new-pos-id" placeholder="e.g., 6377290677581211" className="h-8 font-mono" /></div>
              <div className="w-32"><Label className="text-xs">Label</Label><Input id="new-pos-label" placeholder="POS 1" className="h-8" /></div>
              <div className="w-36"><Label className="text-xs">Branch</Label>
                <Select onValueChange={(v) => document.getElementById('new-pos-branch').value = v === "none" ? "" : v}><SelectTrigger className="h-8"><SelectValue placeholder="Branch" /></SelectTrigger><SelectContent><SelectItem value="none">Select</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
                <input type="hidden" id="new-pos-branch" />
              </div>
              <Button size="sm" className="h-8 rounded-xl" onClick={() => {
                const mid = document.getElementById('new-pos-id').value;
                const label = document.getElementById('new-pos-label').value;
                const bid = document.getElementById('new-pos-branch').value;
                if (!mid) { toast.error('Enter machine ID'); return; }
                savePosMapping(mid, bid, label);
                document.getElementById('new-pos-id').value = '';
                document.getElementById('new-pos-label').value = '';
              }}>Add</Button>
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {posMachines.map(pm => (
                <PosRow key={pm.machine_id} pm={pm} branches={branches} onSave={savePosMapping} onDelete={async () => { await api.delete(`/pos-machines/${pm.machine_id}`); fetchData(); }} />
              ))}
              {(() => {
                const mapped = new Set(posMachines.map(p => p.machine_id));
                const unmapped = new Set();
                statements.forEach(s => { if (s.pos_machines) Object.keys(s.pos_machines).forEach(m => { if (!mapped.has(m)) unmapped.add(m); }); });
                if (detail?.pos_machines) Object.keys(detail.pos_machines).forEach(m => { if (!mapped.has(m)) unmapped.add(m); });
                return [...unmapped].map(mid => (
                  <div key={mid} className="flex gap-2 items-center p-2 border rounded-xl bg-warning/5">
                    <span className="font-mono text-xs flex-1">{mid}</span>
                    <Badge className="bg-warning/20 text-warning">Unmapped</Badge>
                    <Select onValueChange={(v) => savePosMapping(mid, v === "none" ? "" : v, "")}>
                      <SelectTrigger className="w-32 h-7"><SelectValue placeholder="Assign" /></SelectTrigger>
                      <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ));
              })()}
            </div>
          </DialogContent>
        </Dialog>

        <BankPaymentDialog
          open={bankPaymentOpen}
          onOpenChange={setBankPaymentOpen}
          seedFromTxn={bankPaymentSeed}
          onCreated={() => fetchData()}
        />

        <BankCodeDictionaryDialog
          open={dictState.open}
          defaultNewCode={dictState.code}
          defaultNewLabel={dictState.label}
          onClose={() => setDictState({ open: false, code: '', label: '' })}
          onDirty={async () => {
            // Re-fetch the open statement so new overrides re-decode rows.
            if (detail?.id) {
              try {
                const dR = await api.get(`/bank-statements/${detail.id}`);
                setDetail(dR.data);
              } catch { /* non-fatal */ }
            }
          }}
        />

        {/* iter715 Phase C — Find-match candidates picker */}
        <MatchCandidatesDialog
          open={matchDialog.open}
          onOpenChange={(o) => setMatchDialog((m) => ({ ...m, open: o }))}
          statementId={detail?.id}
          txnIdx={matchDialog.txnIdx}
          onMatched={() => detail?.id && viewDetail(detail.id)}
        />

        {/* iter538 — Branch picker before booking fee category as Expense.
            Shows the months that will be booked + total amount + a branch
            select. The user clicks Confirm → one expense per month created. */}
        <Dialog open={!!feeBookingDialog} onOpenChange={(v) => !v && setFeeBookingDialog(null)}>
          <DialogContent className="max-w-md" data-testid="fee-booking-dialog">
            <DialogHeader>
              <DialogTitle>Add {feeBookingDialog?.label} to Expenses</DialogTitle>
            </DialogHeader>
            {feeBookingDialog && (
              <div className="space-y-3 text-sm">
                <div className="text-xs text-muted-foreground">
                  This will create <strong>one expense per month</strong> (date = last day of the month) so
                  the totals show up correctly in P&amp;L without you typing each one. Re-uploading this
                  statement won&apos;t double-book.
                </div>

                <div className="rounded-lg border bg-muted/40 max-h-44 overflow-y-auto">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/60 text-[10px] uppercase">
                        <tr><th className="text-left p-2">Month</th><th className="text-right p-2">Bank txns</th><th className="text-right p-2">Amount</th></tr>
                      </thead>
                      <tbody>
                        {feeBookingDialog.months.map((m) => (
                          <tr key={m.ym} className="border-t" data-testid={`fee-booking-row-${m.ym}`}>
                            <td className="p-2 font-mono">{m.ym}</td>
                            <td className="p-2 text-right text-muted-foreground">{m.txns}</td>
                            <td className="p-2 text-right font-semibold">{cur()} {m.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t bg-muted/40">
                          <td className="p-2 font-bold">Total</td>
                          <td></td>
                          <td className="p-2 text-right font-bold">{cur()} {feeBookingDialog.totalAmount.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Branch (will own these expenses) *</Label>
                  <Select
                    value={feeBookingDialog.branchId || 'none'}
                    onValueChange={(v) => setFeeBookingDialog({ ...feeBookingDialog, branchId: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="fee-booking-branch-select">
                      <SelectValue placeholder="Pick a branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific branch (company-wide)</SelectItem>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Pick the branch you want the bank fees to appear under in P&amp;L. You can leave blank if it&apos;s shared overhead.
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setFeeBookingDialog(null)} disabled={!!feeBookingLoading}>Cancel</Button>
              <Button
                onClick={confirmBookFeeCategory}
                disabled={!!feeBookingLoading}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="fee-booking-confirm"
              >
                {feeBookingLoading
                  ? <><Loader2 size={14} className="mr-1 animate-spin" /> Adding…</>
                  : `Add ${feeBookingDialog?.months?.length || 0} expense${(feeBookingDialog?.months?.length || 0) === 1 ? '' : 's'}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter881 — Skipped rows debug drawer */}
        <Sheet open={showSkippedDrawer} onOpenChange={setShowSkippedDrawer}>
          <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="skipped-rows-drawer">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" />
                Skipped rows ({lastUploadResult?.skipped_count || 0})
              </SheetTitle>
              <SheetDescription>
                These rows in <span className="font-medium">{lastUploadResult?.file_name}</span> could not be parsed and were dropped so the rest of the statement could upload. Common reasons: unparseable date, zero debit/credit, missing reference, malformed amount.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-2">
              {(lastUploadResult?.skipped_rows || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No details available.</p>
              ) : (
                (lastUploadResult?.skipped_rows || []).map((row, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-border p-3 bg-muted/30"
                    data-testid={`skipped-row-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge variant="outline" className="text-[10px] font-mono">Row #{row.row_index ?? '?'}</Badge>
                      <span className="text-xs font-medium text-rose-700 dark:text-rose-400">{row.reason || 'unknown reason'}</span>
                    </div>
                    <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-all text-foreground/80 font-mono bg-background/50 rounded px-2 py-1.5 border border-border/50" data-testid={`skipped-row-raw-${i}`}>
                      {row.raw || '(empty)'}
                    </pre>
                  </div>
                ))
              )}
              {(lastUploadResult?.skipped_count || 0) > (lastUploadResult?.skipped_rows?.length || 0) && (
                <p className="text-xs text-muted-foreground mt-2">
                  Showing first {lastUploadResult?.skipped_rows?.length || 0} of {lastUploadResult?.skipped_count} skipped rows. Full list stored on statement doc.
                </p>
              )}
            </div>
          </SheetContent>
        </Sheet>

      </div>
    </DashboardLayout>
  );
}
